/**
 * Export family data to CSV files
 */

// Helper to escape CSV values
function escapeCSV(value) {
  if (value === null || value === undefined) return '';
  const str = String(value);
  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

// Convert array of objects to CSV string
function toCSV(data, columns) {
  const header = columns.map(col => escapeCSV(col.label)).join(',');
  const rows = data.map(row => 
    columns.map(col => escapeCSV(col.getValue(row))).join(',')
  );
  return [header, ...rows].join('\n');
}

// Download a string as a file
function downloadFile(content, filename, mimeType = 'text/csv') {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// Format address from household
function formatAddress(household) {
  if (!household) return '';
  const parts = [
    household.addressLine1,
    household.addressLine2,
    household.city,
    household.state,
    household.postalCode,
    household.country,
  ].filter(Boolean);
  return parts.join(', ');
}

// Export persons to CSV
export function exportPersonsToCSV(persons) {
  const columns = [
    { label: 'First Name', getValue: p => p.firstName },
    { label: 'Middle Name', getValue: p => p.middleName },
    { label: 'Last Name', getValue: p => p.lastName },
    { label: 'Preferred Name', getValue: p => p.preferredName },
    { label: 'Email', getValue: p => p.email },
    { label: 'Phone', getValue: p => p.phone },
    { label: 'Birthday', getValue: p => p.birthday },
    { label: 'Deceased Date', getValue: p => p.deceasedDate },
    { label: 'Household', getValue: p => p.household?.name },
    { label: 'Address', getValue: p => formatAddress(p.household) },
    { label: 'Notes', getValue: p => p.notes },
  ];
  
  const csv = toCSV(persons, columns);
  const date = new Date().toISOString().split('T')[0];
  downloadFile(csv, `reinhard-family-members-${date}.csv`);
}

// Export relationships to CSV
export function exportRelationshipsToCSV(relationships) {
  const getPersonName = (person) => {
    if (!person) return '';
    return person.preferredName || `${person.firstName} ${person.lastName}`;
  };

  const columns = [
    { label: 'Person 1', getValue: r => getPersonName(r.fromPerson) },
    { label: 'Person 2', getValue: r => getPersonName(r.toPerson) },
    { label: 'Relationship Type', getValue: r => r.type },
  ];
  
  const csv = toCSV(relationships, columns);
  const date = new Date().toISOString().split('T')[0];
  downloadFile(csv, `reinhard-family-relationships-${date}.csv`);
}

// Export households to CSV
export function exportHouseholdsToCSV(households) {
  const columns = [
    { label: 'Name', getValue: h => h.name },
    { label: 'Address Line 1', getValue: h => h.addressLine1 },
    { label: 'Address Line 2', getValue: h => h.addressLine2 },
    { label: 'City', getValue: h => h.city },
    { label: 'State', getValue: h => h.state },
    { label: 'Postal Code', getValue: h => h.postalCode },
    { label: 'Country', getValue: h => h.country },
    { label: 'Members', getValue: h => h.members?.map(m => m.preferredName || `${m.firstName} ${m.lastName}`).join('; ') },
  ];
  
  const csv = toCSV(households, columns);
  const date = new Date().toISOString().split('T')[0];
  downloadFile(csv, `reinhard-family-households-${date}.csv`);
}

// Export all data as a ZIP or multiple downloads
export function exportAllData(persons, relationships, households) {
  exportPersonsToCSV(persons);
  // Small delay between downloads to avoid browser blocking
  setTimeout(() => exportRelationshipsToCSV(relationships), 100);
  setTimeout(() => exportHouseholdsToCSV(households), 200);
}

